// Session authority, tap-only media entry, and focus-preserving rejoin controls.
import { api, beacon, wsUrl } from "../api.js";
import { register, go, handleError, $ } from "../ui.js";
import { SessionLink } from "../session-link.js";
import { Call } from "../call.js";
import { mmss } from "../format.js";
import { TEST_MODE, prepareTestMedia, noteSession } from "../test-media.js";
import { recoverQueue, stopQueue } from '../queue.js';

const WARN_MS = 2 * 60 * 1000;
const IN_SESSION = new Set(["permission", "call"]);
let s = null;
let screen = null;
let navigation = 0;
let loading = null;
let timerInterval = null;

const owns = (owner) => !!owner && s === owner && owner.state.status !== "ended";
const attemptIs = (owner, call, attempt) => owns(owner) && owner.call === call && owner.attempt === attempt;

function teardown() {
  loading?.abort();
  loading = null;
  clearInterval(timerInterval);
  const owner = s;
  s = null; // invalidate callbacks before media/socket cleanup
  if (!owner) return;
  ++owner.attempt;
  owner.tokenRequest?.abort();
  owner.pollRequest?.abort();
  clearTimeout(owner.pollTimer);
  owner.link?.stop();
  owner.call?.leave();
  noteSession(null);
}

async function joinSession(sessionId, epoch) {
  if (s?.sessionId === sessionId) return s;
  teardown();
  const request = new AbortController();
  loading = request;
  const view = await api(`/api/session/${sessionId}`, { signal: request.signal });
  if (loading !== request || navigation !== epoch || screen !== "permission") return null;
  loading = null;
  const owner = { sessionId, role: view.role, mode: view.mode, state: view,
    call: null, attempt: 0, revision: 0, pollEpoch: 0, busy: false, action: null,
    resumed: false, failed: false, error: "" };
  s = owner;
  view.clockOffset = view.serverNow - Date.now();
  noteSession(sessionId);
  if (view.status !== "ended") {
    owner.link = new SessionLink(sessionId, (state) => onState(owner, state), {
      onReconnect: () => {
        if (owns(owner) && !owner.checking) reconcile(owner);
      },
      onEnded: () => {
        if (!owns(owner)) return;
        reconcile(owner);
        if (screen === "permission") renderPermission();
      },
    });
    noteSession(sessionId, owner.link);
  }
  return owner;
}

window.addEventListener("pagehide", (e) => {
  if (s && !e.persisted && s.state.status !== "ended") beacon(`/api/session/${s.sessionId}/leave`);
});

function onState(owner, state) {
  if (s !== owner) return;
  // A terminal state is never locally invented from a 409 or a dropped room.
  owner.state = state;
  ++owner.revision;
  if (state.status === "ended") {
    const { sessionId, role } = owner;
    const cancelled = state.cancelled === true || ["listener_cancelled", "talker_cancelled"].includes(state.endReason);
    ended.add(sessionId);
    teardown();
    if (!state.talkerConnectedAt && (!cancelled || (role === 'talker' && state.endReason === 'listener_cancelled'))) {
      void recoverQueue(sessionId); return;
    }
    stopQueue();
    return cancelled ? go("role") : go("end", { sessionId, role });
  }
  if (state.talkerConnectedAt) stopQueue();
  if (screen === "permission") renderPermission();
  if (screen === "call") renderCall();
}

/** One bounded authoritative reconciliation; superseded requests cannot mutate UI. */
function reconcile(owner) {
  if (!owns(owner)) return;
  owner.pollRequest?.abort();
  clearTimeout(owner.pollTimer);
  const epoch = ++owner.pollEpoch;
  const attempt = owner.attempt;
  owner.checking = true;
  let count = 0;
  const active = () => owns(owner) && owner.pollEpoch === epoch && owner.attempt === attempt;
  const poll = async () => {
    if (!active()) return;
    const request = new AbortController();
    owner.pollRequest = request;
    const revision = owner.revision;
    const timeout = setTimeout(() => request.abort(), 6000);
    try {
      const view = await api(`/api/session/${owner.sessionId}`, { signal: request.signal });
      if (!active() || owner.pollRequest !== request) return;
      if (view.status === "ended" || owner.revision === revision) {
        onState(owner, { ...view, clockOffset: view.serverNow - Date.now() });
      }
    } catch (e) {
      if (active() && e.status === 401) handleError(e);
    } finally {
      clearTimeout(timeout);
      if (owner.pollRequest === request) owner.pollRequest = null;
      if (active()) {
        if (++count < 10) owner.pollTimer = setTimeout(poll, 1500);
        else {
          owner.checking = false;
          if (owner.link?.closed) {
            owner.failed = true;
            showError(owner, "couldn't check the session. try again.");
          }
        }
      }
    }
  };
  poll();
}

function showError(owner, message) {
  if (!owns(owner)) return;
  owner.error = message;
  if (screen === "permission") renderPermission();
  else if (screen === "call") renderCall();
}

function renderPermission() {
  const btn = $("permission-start");
  btn.hidden = false; // the waiting action never moves/disappears
  $("permission-wait").hidden = true;
  const owner = s;
  const waiting = !owner || (owner.role === "talker" && !owner.state.listenerInCall);
  const closed = owner?.link?.closed;
  btn.disabled = !owner ||
    (closed ? owner.checking : waiting || owner.busy || !!owner.action || !!owner.call) ||
    (!!owner?.state.policyVersion && !owner.state.consentAcknowledged);
  $('permission-consent').checked = !!owner?.state.consentAcknowledged || !!owner?.consentBusy;
  $('permission-consent').disabled = !owner || !!owner.consentBusy || !!owner.state.consentAcknowledged;
  const deadline = owner?.state.offerExpiresAt ?? owner?.state.talkerJoinUntil;
  $('permission-deadline').textContent = deadline ? mmss(Math.max(0, deadline - Date.now() - (owner.state.clockOffset ?? 0))) + ' to join' : '';
  btn.textContent = owner?.busy ? (owner.role === "listener" ? "starting…" : "joining…")
    : closed ? "try again" : waiting ? "waiting for listener…" : owner.failed ? "try again"
    : owner.resumed ? "rejoin call" : owner.role === "listener" ? "start call" : "join call";
  $("permission-error").textContent = owner?.error || "";
  $("permission-error").hidden = !owner?.error;
  const cancel = $("permission-cancel");
  if (cancel) {
    cancel.hidden = !owner || !!owner.state.talkerConnectedAt;
    cancel.disabled = !owner || !!owner.action;
  }
}

function mediaFailed(owner, call, attempt, state) {
  if (!attemptIs(owner, call, attempt)) return;
  reportFailure(owner, state === 'permission_denied' ? 'permission_denied' : 'media_failed');
  owner.link?.setMediaReady(false);
  if (owner.role === "listener") owner.state.listenerInCall = false;
  owner.failed = true;
  owner.error = state === "permission_denied"
    ? "camera or microphone blocked — allow access in your browser settings and tap again."
    : "call disconnected. tap to try again.";
  call.leave();
  // During join its own rejection/finally owns the transition and busy flag.
  if (!owner.busy) {
    owner.call = null;
    ++owner.attempt;
    if (screen === "call") go("permission", { sessionId: owner.sessionId, resumed: true, failed: true });
    else if (screen === "permission") renderPermission();
  }
  reconcile(owner);
}

$("permission-start").addEventListener("click", async () => {
  const owner = s;
  if (screen === "permission" && owns(owner) && owner.link?.closed) {
    if (!owner.checking) { reconcile(owner); renderPermission(); }
    return; // a terminal control socket never authorizes another media join
  }
  if (screen !== "permission" || !owns(owner) || owner.busy || owner.call || owner.action ||
      (owner.state.policyVersion && !owner.state.consentAcknowledged) ||
      (owner.role === "talker" && !owner.state.listenerInCall)) return;
  const attempt = ++owner.attempt;
  owner.busy = true;
  owner.error = "";
  // Synchronous gesture work before token/media promises (Safari audio unlock).
  prepareTestMedia(owner.role);
  const call = new Call({
    mode: owner.mode,
    remoteVideo: $("remote-video"), selfVideo: $("self-video"), remoteAudio: $("remote-audio"),
    onAutoplayBlocked: () => {
      if (attemptIs(owner, call, attempt)) $("call-sound").hidden = false;
    },
    onLeft: (state) => mediaFailed(owner, call, attempt, state),
  });
  owner.call = call;
  if (TEST_MODE) {
    owner.mediaAttempts = [...(owner.mediaAttempts || []), call].slice(-3);
  }
  call.unlockAudio();
  renderPermission();
  const request = new AbortController();
  owner.tokenRequest = request;
  try {
    const { authToken } = await api(`/api/session/${owner.sessionId}/token`, { method: "POST", signal: request.signal });
    if (!attemptIs(owner, call, attempt) || screen !== "permission") { call.leave(); return; }
    await call.join(authToken);
    if (!attemptIs(owner, call, attempt) || screen !== "permission") { call.leave(); return; }
    if (owner.link.closed) throw new Error("session unavailable");
    call.assertMediaReady();
    owner.link.setMediaReady(true);
    owner.failed = false;
    owner.busy = false;
    go("call");
  } catch (e) {
    call.leave();
    if (!attemptIs(owner, call, attempt) || screen !== "permission") return;
    owner.call = null;
    owner.link?.setMediaReady(false);
    if (owner.role === "listener") owner.state.listenerInCall = false;
    owner.failed = true;
    if (e.status === 401) return handleError(e);
    if (e.data?.error === "listener_not_ready") {
      owner.state.listenerInCall = false;
      owner.error = "";
    } else if (e.data?.error === "ended") {
      owner.error = "session ended.";
      reconcile(owner);
    } else {
      console.error(e);
      const blocked = e?.name === "NotAllowedError" || /permission.*(denied|canceled)/i.test(String(e?.message));
      reportFailure(owner, blocked ? 'permission_denied' : 'media_failed');
      owner.error = blocked
        ? "camera or microphone blocked — allow access in your browser settings and tap again."
        : "couldn't start the call. tap to try again.";
    }
  } finally {
    if (s === owner && owner.attempt === attempt) {
      owner.busy = false;
      if (owner.tokenRequest === request) owner.tokenRequest = null;
      if (screen === "permission") renderPermission();
    }
  }
});

function reportFailure(owner, kind) {
  void api('/api/session/' + owner.sessionId + '/failure', { method: 'POST', body: { kind } }).catch(() => {});
}

function remaining(owner = s) {
  const st = owner?.state;
  return st?.endsAt ? st.endsAt - (Date.now() + (st.clockOffset ?? 0)) : null;
}

function renderCall() {
  const owner = s;
  if (!owns(owner) || screen !== "call") return;
  const st = owner.state;
  const left = remaining(owner);
  const timer = $("call-timer");
  timer.textContent = left === null ? (owner.role === "listener" ? "waiting for talker" : "connecting…") : mmss(left);
  timer.classList.toggle("warning", left !== null && left <= WARN_MS);
  if (left !== null && left <= 0 && !owner.expiryChecked) {
    owner.expiryChecked = true;
    reconcile(owner);
  }
  let banner = "";
  if (owner.role === "talker" && !st.listenerPresent) banner = "listener reconnecting…";
  else if (owner.role === "listener" && st.talkerConnectedAt && !st.talkerPresent) banner = "talker left — they can rejoin";
  else if (left !== null && left <= WARN_MS) banner = "2 minutes left";
  $("call-banner").textContent = banner;
  $("call-banner").hidden = !banner;
  document.querySelector(".call-stage").classList.toggle("video", owner.mode === "video");
  $("remote-label").textContent = owner.role === "listener" ? "talker" : "listener";
  $("call-end").hidden = owner.role !== "listener" || !st.talkerConnectedAt;
  $("call-leave").hidden = owner.role !== "talker";
  $("call-end").disabled = !!owner.action;
  $("call-leave").disabled = !!owner.action;
  const cancel = $("call-cancel");
  if (cancel) {
    cancel.hidden = owner.role !== "listener" || !!st.talkerConnectedAt;
    cancel.disabled = !!owner.action;
  }
  const error = $("call-error");
  if (error) { error.textContent = owner.error; error.hidden = !owner.error; }
}

async function cancelPending() {
  const owner = s;
  if (!owns(owner) || owner.state.talkerConnectedAt || owner.action || !IN_SESSION.has(screen)) return;
  const action = {};
  owner.action = action;
  owner.error = "";
  // Abandon unfinished media immediately. A late join cannot outrun cancel.
  // Keep an already usable listener call until the server settles the race.
  if (owner.busy) {
    ++owner.attempt;
    owner.tokenRequest?.abort();
    owner.call?.leave();
    owner.call = null;
    owner.busy = false;
    owner.failed = true;
    owner.link?.setMediaReady(false);
  }
  if (screen === "call") renderCall(); else renderPermission();
  try {
    const result = await api(`/api/session/${owner.sessionId}/cancel`, { method: "POST" });
    if (!owns(owner) || owner.action !== action) return;
    if (result.ok !== true) throw new Error("cancel not confirmed");
    // The serialized cancel endpoint confirms final cancellation only; if
    // first connection wins it returns not_pending instead of ok.
    ended.add(owner.sessionId);
    teardown();
    stopQueue();
    go("role");
  } catch (e) {
    if (!owns(owner) || owner.action !== action) return;
    if (e.status === 401) return handleError(e);
    showError(owner, e.data?.error === "not_pending" ? "call already started." : "couldn't cancel. try again.");
    reconcile(owner);
  } finally {
    if (owns(owner) && owner.action === action) {
      owner.action = null;
      if (screen === "call") renderCall(); else if (screen === "permission") renderPermission();
    }
  }
}
$("permission-cancel")?.addEventListener("click", cancelPending);
$('permission-consent').addEventListener('change', async () => {
  const owner = s;
  if (!owns(owner) || screen !== 'permission' || owner.consentBusy || !$('permission-consent').checked) return;
  owner.consentBusy = true; renderPermission();
  try {
    await api('/api/session/' + owner.sessionId + '/consent', { method: 'POST', body: { version: owner.state.policyVersion } });
    if (owns(owner)) owner.state.consentAcknowledged = true;
  } catch { showError(owner, 'couldn’t save your acknowledgement. please try again.'); }
  finally { owner.consentBusy = false; if (owns(owner) && screen === 'permission') renderPermission(); }
});
$("call-cancel")?.addEventListener("click", cancelPending);

$("call-end").addEventListener("click", async () => {
  const owner = s;
  if (!owns(owner) || screen !== "call" || owner.role !== "listener" || !owner.state.talkerConnectedAt || owner.action) return;
  const action = {};
  owner.action = action;
  owner.error = "";
  renderCall();
  try {
    await api(`/api/session/${owner.sessionId}/end`, { method: "POST" });
    if (owns(owner) && owner.action === action) reconcile(owner);
  } catch (e) {
    if (!owns(owner) || owner.action !== action) return;
    if (e.status === 401) return handleError(e);
    showError(owner, "couldn't end the call. try again.");
  } finally {
    if (owns(owner) && owner.action === action) { owner.action = null; renderCall(); }
  }
});

$("call-leave").addEventListener("click", () => {
  const owner = s;
  if (!owns(owner) || screen !== "call" || owner.role !== "talker" || owner.action) return;
  owner.link?.setMediaReady(false);
  owner.link?.send("leave");
  const id = owner.sessionId;
  teardown();
  // Navigate immediately; this result must never move a newer screen.
  api(`/api/session/${id}/leave`, { method: "POST" }).catch(() => {});
  go("role");
});

$("call-sound").addEventListener("click", async () => {
  const owner = s, call = owner?.call, attempt = owner?.attempt;
  const enabled = await call?.enableSound();
  if (enabled && attemptIs(owner, call, attempt) && screen === "call") $("call-sound").hidden = true;
});

// Current-session items are keyed: one-second countdowns preserve focus.
let current = [];
const ended = new Set();
const watchers = new Map();
const currentNodes = new Map();
let onRole = false;
let roleEpoch = 0;
let currentTick = null, currentPoll = null, watchPing = null, currentRequest = null;

function renderCurrent() {
  if (!onRole) return;
  const now = Date.now();
  current = current.filter((c) => !ended.has(c.id) && (!c.endsAt || c.endsAt - (now + c.offset) > 0));
  const ids = new Set(current.map((c) => c.id));
  for (const [id, node] of currentNodes) {
    if (!ids.has(id)) { node.li.remove(); currentNodes.delete(id); unwatch(id); }
  }
  for (const c of current) {
    let node = currentNodes.get(c.id);
    if (!node) {
      const li = document.createElement("li"), mode = document.createElement("p"), btn = document.createElement("button");
      btn.type = "button";
      btn.className = "primary";
      btn.addEventListener("click", () => {
        if (onRole && current.some((x) => x.id === c.id) && !ended.has(c.id)) {
          go("permission", { sessionId: c.id, resumed: true });
        }
      });
      li.append(mode, btn);
      $("current-list").append(li);
      node = { li, mode, btn };
      currentNodes.set(c.id, node);
    }
    node.mode.textContent = c.mode === "video" ? "video call" : "audio call";
    node.btn.textContent = c.endsAt ? `rejoin (${mmss(c.endsAt - (now + c.offset))} left)` : "rejoin";
  }
  $("current-title").textContent = current.length > 1 ? "current sessions" : "current session";
  $("current").hidden = current.length === 0;
}

function dropCurrent(id) {
  ended.add(id);
  unwatch(id);
  renderCurrent();
}

function watch(id) {
  if (!onRole || watchers.has(id)) return;
  const epoch = roleEpoch;
  let ws;
  try { ws = new WebSocket(wsUrl(`/api/session/${id}/ws?watch=1`)); } catch { return; }
  watchers.set(id, ws);
  const active = () => onRole && roleEpoch === epoch && watchers.get(id) === ws;
  ws.onmessage = (e) => {
    if (!active()) return;
    let msg;
    try { msg = JSON.parse(e.data); } catch { return; }
    if (msg.type !== "state" || !msg.state) return;
    if (msg.state.status === "ended") {
      const feedbackEligible = !!msg.state.talkerConnectedAt &&
        !["listener_cancelled", "talker_cancelled"].includes(msg.state.endReason);
      dropCurrent(id);
      if (feedbackEligible) go("end", { sessionId: id });
      return;
    }
    const c = current.find((x) => x.id === id);
    if (c) {
      c.endsAt = msg.state.endsAt;
      c.offset = msg.state.serverNow - Date.now();
      ++c.revision;
      renderCurrent();
    }
  };
  ws.onclose = (e) => {
    if (!active()) return;
    watchers.delete(id);
    if (e.code === 4001) void loadCurrent(); // reconcile missed final state through /api/me
  };
  ws.onerror = () => { if (active()) { try { ws.close(); } catch {} } };
}

function unwatch(id) {
  const ws = watchers.get(id);
  watchers.delete(id);
  try { ws?.close(1000); } catch {}
}

async function loadCurrent() {
  if (!onRole || currentRequest) return;
  const epoch = roleEpoch;
  const request = new AbortController();
  currentRequest = request;
  const revisions = new Map(current.map((c) => [c.id, c.revision]));
  const timeout = setTimeout(() => request.abort(), 8000);
  try {
    const { current: list, resume } = await api("/api/me", { signal: request.signal });
    if (!onRole || roleEpoch !== epoch || currentRequest !== request) return;
    if (!Array.isArray(list)) throw new Error("invalid current sessions response");
    // Polling also handles a missed watcher event or a return from background.
    // Never steal another screen: this request is owned by the role epoch.
    if (resume?.kind === "end" && !ended.has(resume.sessionId)) {
      ended.add(resume.sessionId);
      go("end", { sessionId: resume.sessionId });
      return;
    }
    current = list.filter((c) => !ended.has(c.id)).map((c) => {
      const previous = current.find((x) => x.id === c.id);
      if (previous && previous.revision !== revisions.get(c.id)) return previous;
      return { ...c, offset: c.serverNow - Date.now(), revision: previous?.revision ?? 0 };
    });
    for (const id of watchers.keys()) if (!current.some((c) => c.id === id)) unwatch(id);
    for (const c of current) watch(c.id);
    renderCurrent();
  } catch (e) {
    if (onRole && roleEpoch === epoch && currentRequest === request && e.name !== "AbortError") handleError(e);
  } finally {
    clearTimeout(timeout);
    if (currentRequest === request) currentRequest = null;
  }
}

register({
  role: {
    enter() {
      teardown();
      onRole = true;
      const epoch = ++roleEpoch;
      current = [];
      renderCurrent();
      loadCurrent();
      currentTick = setInterval(() => { if (onRole && roleEpoch === epoch) renderCurrent(); }, 1000);
      currentPoll = setInterval(() => { if (onRole && roleEpoch === epoch) loadCurrent(); }, 5000);
      watchPing = setInterval(() => {
        if (!onRole || roleEpoch !== epoch) return;
        for (const [id, ws] of watchers) {
          if (watchers.get(id) === ws && ws.readyState === WebSocket.OPEN) {
            try { ws.send('{"type":"ping"}'); } catch { unwatch(id); }
          }
        }
      }, 20000);
    },
    leave() {
      onRole = false;
      ++roleEpoch;
      currentRequest?.abort();
      currentRequest = null;
      clearInterval(currentTick);
      clearInterval(currentPoll);
      clearInterval(watchPing);
      for (const id of [...watchers.keys()]) unwatch(id);
    },
  },
  permission: {
    async enter({ sessionId, resumed = false, failed = false } = {}) {
      screen = "permission";
      const epoch = ++navigation;
      if (s?.sessionId !== sessionId) teardown();
      $("permission-title").textContent = resumed ? "your session is open." : "you're matched.";
      $("permission-mode").textContent = "";
      $("permission-error").hidden = true;
      $("call-sound").hidden = true;
      renderPermission();
      try {
        const owner = await joinSession(sessionId, epoch);
        if (!owner || navigation !== epoch || screen !== "permission" || s !== owner) return;
        if (owner.state.status === "ended") return onState(owner, owner.state);
        owner.resumed = resumed;
        if (!failed) { owner.failed = false; owner.error = ""; }
        $("permission-mode").textContent = owner.mode === "video" ? "video call" : "audio call";
        clearInterval(timerInterval);
        timerInterval = setInterval(() => { if (owns(owner) && screen === 'permission') renderPermission(); }, 1000);
        renderPermission();
      } catch (e) {
        if (navigation !== epoch || screen !== "permission" || e.name === "AbortError") return;
        if (e.status === 401) return handleError(e);
        console.error(e);
        go("role");
      }
    },
    leave(next) {
      screen = null;
      ++navigation;
      clearInterval(timerInterval);
      loading?.abort();
      loading = null;
      if (!IN_SESSION.has(next)) teardown();
    },
  },
  call: {
    enter() {
      screen = "call";
      ++navigation;
      if (!owns(s) || !s.call?.ready) {
        if (s) go("permission", { sessionId: s.sessionId, resumed: true, failed: true });
        else go("role");
        return;
      }
      s.error = "";
      renderCall();
      clearInterval(timerInterval);
      const owner = s;
      timerInterval = setInterval(() => { if (owns(owner)) renderCall(); }, 500);
    },
    leave(next) {
      screen = null;
      ++navigation;
      clearInterval(timerInterval);
      if (!IN_SESSION.has(next)) teardown();
    },
  },
});

if (TEST_MODE && window.mitranTest) {
  window.mitranTest.reliability = {
    snapshot: () => ({ screen, sessionId: s?.sessionId, attempt: s?.attempt, busy: s?.busy,
      ready: s?.call?.ready ?? false, sticky: s?.link ? [...s.link.extraOnOpen.keys()] : [],
      currentIds: current.map((c) => c.id), polling: !!currentRequest,
      mediaAttempts: (s?.mediaAttempts || []).map((call) => call.testMediaSnapshot()) }),
    mediaLeft: (state = "failed") => { if (s?.call) { s.call.fail(state); return true; } return false; },
    assertMediaReady: () => s?.call?.assertMediaReady() ?? false,
    stopTrack: (kind) => {
      if (kind !== "audio" && kind !== "video") return false;
      const call = s?.call, track = call?.meeting?.self[`${kind}Track`];
      if (!track) return false;
      track.stop(); // track.stop() itself does not dispatch the ended event
      track.dispatchEvent(new Event("ended"));
      return true;
    },
    // Test-only navigation exercises retained feedback queues across view epochs.
    openEnd: (sessionId) => {
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(sessionId)) return false;
      go("end", { sessionId });
      return true;
    },
    reconnectControl: () => s?.link?.connect(),
    forgetSticky: (type) => s?.link?.forgetSticky(type),
    setMediaReady: (ready) => s?.link?.setMediaReady(ready),
  };
}
